// Gate wrapper composed by AdminShell. Owns:
//   - usePublisherConfig loading + redirect when slug doesn't exist
//   - Redirect to /admin when signed out (single sign-in surface)
//   - Grant gate (CTV-415 Phase 2b) → /access-denied for ungranted staff
//   - canAdminStation gate → /admin (picker) for users without scope
//
// Sign-in lives only at /admin. Deep-links are preserved via location state:
// `state.from` carries the original path through the sign-in round trip;
// AdminEntryPage redirects back there after the user authenticates.

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePublisherConfig } from '../../../data/publisherConfigs';
import { useAuth, type SelfServiceUser } from '../auth/useAuth';
import type { PublisherConfig } from '../../../types/selfService';

interface Props {
  slug: string | undefined;
  children: (ctx: { config: PublisherConfig; user: SelfServiceUser; slug: string }) => React.ReactNode;
}

const PublisherAdminGate: React.FC<Props> = ({ slug, children }) => {
  const location = useLocation();
  const { config, loading } = usePublisherConfig(slug);
  const { user, isAuthenticated, canAdminStation, hasPublisherAdminGrant } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!slug || !config) return <Navigate to="/" replace />;

  if (!isAuthenticated || !user) {
    // Single sign-in surface lives at /admin. Pass the deep-link target so
    // AdminEntryPage can route the user back here after authenticating.
    return (
      <Navigate
        to="/admin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // CTV-415 Phase 2b grant gate. Super_admins bypass entirely (they implicitly
  // hold the grant; the RPC short-circuits on is_super_admin). Other staff
  // need an active selfservice_publisher_admin_grants row issued by a
  // super_admin from the dashboard's Settings → Users. The RPC errors deny
  // by default (useAuth sets `false` on error) so a transient supabase outage
  // routes staff to /access-denied instead of accidentally letting them in.
  if (user.rawRole !== 'super_admin' && hasPublisherAdminGrant === false) {
    return <Navigate to="/access-denied" replace />;
  }

  if (!canAdminStation({
    name: config.name,
    organizationId: config.organizationId,
  })) {
    // CTV-415 — user is authed but not authorized for this slug. Send them
    // to the picker so they can pick a station they actually have access
    // to. Friendlier than a dead-end "No access" card.
    return <Navigate to="/admin" replace />;
  }

  return <>{children({ config, user, slug })}</>;
};

export default PublisherAdminGate;
